
/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import {signal} from '@preact/signals-react';
import {ColumnState} from 'ag-grid-community';
import {createMutator} from '@ion/applications/src/store/SignalUtils';
import {translate} from '@ion/core/src/locale/locale';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {GenericObject, ScheduledReport} from '@ion/core/src/types/entities';
import {Preference} from '@ion/core/src/types/preferences';
import {FilterModel} from '@ion/dls/src/grid/IonGridUtils';
import {SearchType} from './SearchTypes';
import {Headertabs, ScheduledReportsEntryMode, SideNavTabs} from './types';

// === Signals and Mutators ===
export const mobileOpen    = signal<boolean>(false);
export const setMobileOpen = createMutator<boolean>(mobileOpen);

export const openCategory    = signal<GenericObject>({});
export const setOpenCategory = createMutator<GenericObject>(openCategory);

export const scheduledReportsOpenCategory    = signal<GenericObject>({});
export const setScheduledReportsOpenCategory = createMutator<GenericObject>(scheduledReportsOpenCategory);

export const selectedCategory    = signal<GenericObject>({});
export const setSelectedCategory = createMutator<GenericObject>(selectedCategory);

export const scheduledReportsSelectedCategory    = signal<GenericObject>({});
export const setScheduledReportsSelectedCategory = createMutator<GenericObject>(scheduledReportsSelectedCategory);

export interface ScheduledReportsContext {
  entryMode: ScheduledReportsEntryMode;
  originHeaderTab: Headertabs | null;
}

export const autoSelectCategory    = signal<boolean>(false);
export const setAutoSelectCategory = createMutator<boolean>(autoSelectCategory);

export const selectedDashboard    = signal<any>(null);
export const setSelectedDashboard = createMutator<any>(selectedDashboard);

export const sideTab    = signal<SideNavTabs>(SideNavTabs.Basic);
export const setSideTab = createMutator<SideNavTabs>(sideTab);

export const headerTab    = signal<Headertabs>(Headertabs.All);
export const setHeaderTab = createMutator<Headertabs>(headerTab);

export const lastDashboardHeaderTab    = signal<Headertabs>(Headertabs.All);
export const setLastDashboardHeaderTab = createMutator<Headertabs>(lastDashboardHeaderTab);

export const scheduledReportsContext = signal<ScheduledReportsContext>({
  entryMode       : 'direct',
  originHeaderTab : null
});
export const setScheduledReportsContext = createMutator<ScheduledReportsContext>(scheduledReportsContext);

export const searchValue    = signal<string>('');
export const setSearchValue = createMutator<string>(searchValue);

export const scheduledReportsSearchInputValue    = signal<string>('');
export const setScheduledReportsSearchInputValue = createMutator<string>(scheduledReportsSearchInputValue);

export const scheduledReportsSearchValue    = signal<string>('');
export const setScheduledReportsSearchValue = createMutator<string>(scheduledReportsSearchValue);

export const categoryFilter = signal<GenericObject>({});
export const setCategoryFilter = createMutator<GenericObject>(categoryFilter);

export const scheduledReportGridFilterModel    = signal<FilterModel>(null);
export const setScheduledGridFilterModel = createMutator<FilterModel>(scheduledReportGridFilterModel);

export const migrationResultGridFilterModel    = signal<FilterModel>(null);
export const setMigrationResultGridFilterModel = createMutator<FilterModel>(migrationResultGridFilterModel);

export const migrationDetailGridFilterModel    = signal<FilterModel>(null);
export const setMigrationDetailGridFilterModel = createMutator<FilterModel>(migrationDetailGridFilterModel);

export const scheduledGridColumnState    = signal<ColumnState[]>(null);
export const setScheduledGridColumnState = createMutator<ColumnState[]>(scheduledGridColumnState);

export const migrationGridColumnState    = signal<ColumnState[]>(null);
export const setMigrationGridColumnState = createMutator<ColumnState[]>(migrationGridColumnState);

export const showAddScheduledReportEditor   = signal<boolean>(false);
export const setShowAddScheduledReportEditor = createMutator<boolean>(showAddScheduledReportEditor);

export const selectedDashboardReport   = signal<any>(null);
export const setSelectedDashboardReport = createMutator<any>(selectedDashboardReport);

export const createScheduledReport    = signal<ScheduledReport>(null);
export const setCreateScheduledReport = createMutator<ScheduledReport>(createScheduledReport);

export const duplicatingScheduledReport    = signal<boolean>(false);
export const setDuplicatingScheduledReport = createMutator<boolean>(duplicatingScheduledReport);

export const editScheduledReport    = signal<ScheduledReport>(null);
export const setEditScheduledReport = createMutator<ScheduledReport>(editScheduledReport);

export const selectedScheduledReport    = signal<ScheduledReport>(null);
export const setSelectedScheduledReport = createMutator<ScheduledReport>(selectedScheduledReport);

export const creatingScheduledReport    = signal<boolean>(false);
export const setCreatingScheduledReport = createMutator<boolean>(creatingScheduledReport);

export const editingScheduledReport    = signal<boolean>(false);
export const setEditingScheduledReport = createMutator<boolean>(editingScheduledReport);

export const deletingScheduledReport    = signal<boolean>(false);
export const setDeletingScheduledReport = createMutator<boolean>(deletingScheduledReport);

/**
 * Navigates to a given report (dashboard) and updates the application state
 * by setting the active header tab, the selected dashboard, and optionally
 * auto-selecting its category.
 *
 * This function is typically called when a user chooses a report from the
 * dashboard list or tree, ensuring that the application UI is synchronized
 * with the chosen context.
 *
 * @param dashboard          - The dashboard object to navigate to and set
 *                             as selected.
 * @param autoSelectCategory - Whether to automatically select the dashboard's
 *                             category (defaults to `true`).
 *
 * ```typescript
 * import {navigateReport} from '@ion/applications/src/pages/analytics/store';
 *
 * // Select a dashboard and auto-select its category
 * navigateReport(dashboard);
 *
 * // Select a dashboard without auto-selecting its category
 * navigateReport(dashboard, false);
 * ```
 */
export const navigateReport = (dashboard, autoSelectCategory = true) => {
  updateHeaderTab(Headertabs.All);
  setSelectedDashboard(dashboard);
  setAutoSelectCategory(autoSelectCategory);
};

/**
 * Updates the active analytics header tab while keeping the last
 * dashboard-backed tab (`All` or `Recent`) in sync for the Scheduled
 * Reports borrowed left-panel view.
 *
 * @param newValue - The header tab to activate.
 */
export const updateHeaderTab = (newValue: Headertabs) => {
  if (newValue === Headertabs.ScheduledReports) {
    setScheduledReportsContext({
      entryMode       : 'direct',
      originHeaderTab : null
    });
    setScheduledReportsOpenCategory({});
    setScheduledReportsSelectedCategory({});
  } else {
    setScheduledReportsContext({
      entryMode       : 'direct',
      originHeaderTab : null
    });
    setLastDashboardHeaderTab(newValue);
  }
  setHeaderTab(newValue);
};

/**
 * Opens Scheduled Reports from a dashboard row action and keeps the
 * originating dashboard tab visually highlighted in the header.
 */
export const openScheduledReportsFromDashboardRow = () => {
  const isScheduledReportsTab = headerTab.value === Headertabs.ScheduledReports;
  setScheduledReportsContext({
    entryMode       : isScheduledReportsTab ? 'direct' : 'dashboardRow',
    originHeaderTab : isScheduledReportsTab ? null : headerTab.value
  });
  if (!isScheduledReportsTab) {
    setScheduledReportsOpenCategory({});
    setScheduledReportsSelectedCategory({});
  }
};

/**
 * Resets the selected dashboard and category if the current side tab 
 * is set to "MyReports". This function is typically used to verify
 * or reset the persistence-related state when switching contexts or
 * cleaning up after a user interaction with "My Reports".
 * 
 * ```typescript
 * 
 * // Call this when switching away from My Reports tab
 * import {resetMyReportsSelection} from '@ion/applications/src/pages/analytics/store';
 * 
 * if (currentGroup !== nextGroup) {
 *   resetMyReportsSelection();
 * }
 * 
 * ```
 */
export const resetMyReportsSelection = () => {
  if (selectedDashboard.value?.categoryName === translate('sisense:analytics.folderNames.myReports')) {
    setSelectedDashboard(null);
  }
};

/**
 * Initializes the analytics store state from persisted user preferences.
 * 
 * This function restores the previously saved values for header tab,
 * side navigation tab, open category, and selected category. If no
 * preferences are found, it falls back to sensible defaults:
 * 
 * - `headerTab`: defaults to `Headertabs.All`
 * - `sideTab`: defaults to `SideNavTabs.Basic`
 * - `openCategory`: defaults to an empty object
 * - `selectedCategory`: defaults to an empty object
 * 
 * This ensures that user preferences are preserved across sessions
 * and the analytics view opens in a consistent state.
 * 
 * ```typescript
 * import {initializeAnalyticsStore} from '@ion/applications/src/pages/analytics/store';
 * 
 * // Call this at application startup to restore analytics preferences
 * initializeAnalyticsStore();
 * ```
 */
export const initializeAnalyticsStore = () => {
  const {
    headerTab: savedHeaderTab = Headertabs.All,
    lastDashboardHeaderTab: savedLastDashboardHeaderTab = Headertabs.All,
    sideTab: savedSideTab = SideNavTabs.Basic,
    openCategory: savedOpenCategory = {},
    selectedCategory: savedSelectedCategory = {},
    categoryFilter: savedCategoryFilter = {}
  } =  UserPreferences.get(Preference.analyticsView) || {};
  const isScheduledReportsView = savedHeaderTab === Headertabs.ScheduledReports;
  const restoredLastDashboardHeaderTab = isScheduledReportsView
    ? savedLastDashboardHeaderTab
    : savedHeaderTab;
  const sanitizedState = {
    headerTab              : savedHeaderTab,
    lastDashboardHeaderTab : restoredLastDashboardHeaderTab,
    sideTab                : savedSideTab,
    openCategory           : isScheduledReportsView ? savedOpenCategory : savedOpenCategory,
    selectedCategory       : isScheduledReportsView ? savedSelectedCategory : savedSelectedCategory,
    categoryFilter         : isScheduledReportsView ? savedCategoryFilter : savedCategoryFilter
  };

  UserPreferences.set(Preference.analyticsView, sanitizedState, true);

  updateHeaderTab(savedHeaderTab);
  setLastDashboardHeaderTab(restoredLastDashboardHeaderTab);
  setSideTab(savedSideTab);
  setOpenCategory(sanitizedState.openCategory);
  setSelectedCategory(sanitizedState.selectedCategory);
  setCategoryFilter(sanitizedState.categoryFilter);
  setScheduledReportsOpenCategory({});
  setScheduledReportsSelectedCategory({});
  setScheduledReportsContext({
    entryMode       : 'direct',
    originHeaderTab : null
  });
};

export const scheduledReportsSearchType = signal<SearchType>(SearchType.Any);

export const setScheduledReportsSearchType = (value: SearchType): void => {
  scheduledReportsSearchType.value = value;
};

export const scheduledReportsSearchTypeInputValue = signal<SearchType>(SearchType.Any);

export const setScheduledReportsSearchTypeInputValue = (value: SearchType): void => {
  scheduledReportsSearchTypeInputValue.value = value;
};
